import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

function SearchableDropdown({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Select an option...",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Reset search when opening
  useEffect(() => {
    if (isOpen) {
      setSearchTerm(value?.name || "");
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setSearchTerm("");
    }
  }, [isOpen, value]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter options
  const filteredOptions = useMemo(() => {
    return options.filter((opt) =>
      opt.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const handleSelect = useCallback(
    (option) => {
      onChange(option);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div className="w-full" ref={dropdownRef}>
      <div className="flex items-center gap-6">
        {/* Label */}
        {label && (
          <label className="text-sm font-semibold text-base-content/70 whitespace-nowrap">
            {label}
          </label>
        )}

        {/* Dropdown Trigger */}
        <div className="relative flex-1">
          <div
            className={`
              w-full px-4 py-2.5 rounded-lg transition-all duration-200 bg-white border border-info/40
              ${
                disabled
                  ? "text-gray-400 cursor-not-allowed"
                  : isOpen
                  ? "ring-2 ring-blue-500/40 shadow-md"
                  : "cursor-pointer hover:bg-gray-50"
              }
            `}
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            <span className={value ? "text-gray-900" : "text-gray-500"}>
              {value?.name || placeholder}
            </span>
          </div>

          {/* Dropdown Menu */}
          {isOpen && !disabled && (
            <div
              className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200"
              style={{ maxHeight: "256px" }} // ~10 items × 40px
            >
              {/* Search Input */}
              <div className="p-2 border-b">
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-2 text-sm rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Scrollable Options List */}
              <div
                className="overflow-y-auto"
                style={{ maxHeight: "216px" }} // 256px - padding - search height
              >
                {filteredOptions.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 text-center">
                    No options found
                  </div>
                ) : (
                  <ul>
                    {filteredOptions.map((opt) => (
                      <li key={opt.id} className="z-50">
                        <span
                          type="button"
                          onClick={() => handleSelect(opt)}
                          className={`w-full text-left px-3 py-2 text-sm ${
                            value?.id === opt.id
                              ? "bg-blue-50 text-blue-700 font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {opt.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(SearchableDropdown);
